import React, { useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import moment from "moment";
import { useGetCostWeeklyQuery } from "store/slices/apis/costSlice";

const formatDate = (date) => {
  if (!date) return "";
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format("DD/MM/YYYY") : "";
};

const Header = ({ title, subtitle, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      textAlign: align,
      display: "flex",
      flexDirection: "column",
      lineHeight: 1.1,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const ScheduleWeeklySection = ({ projectId, activityId, monthMeta }) => {
  const gridCols = "2.2fr 1fr 1fr 1fr 1fr"; 

  const monthStart = useMemo(() => {
    const m = moment(monthMeta?.monthStart);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
}, [monthMeta?.monthStart]);
  console.log("monthStart",monthStart);

  const { data, isFetching, isError, error } = useGetCostWeeklyQuery(
    { projectId, activityId, monthStart },
    { skip: !projectId || !activityId || !monthStart }
  );

  const rows = useMemo(() => {
    // normalize backend shape
    const apiRows = data?.data ?? data ?? [];
    return apiRows.map((w) => ({
      label: w.week ?? w.label ?? `${formatDate(w.weekStart)} - ${formatDate(w.weekEnd)}`,
      BaselineCost_Month: w.baselineCost_Week ?? w.baselineCost_Month ?? "—",
      ActualCost_Month: w.actualCost_Week ?? w.actualCost_Month ?? "—",
      BaselinePlanPct_Month: w.baselinePlanPct_Week ?? w.baselinePlanPct_Month ?? "—",
      ProjectedCompletionPct_Month: w.projectedCompletionPct ?? w.projectedCompletionPct_Month ?? "—",
      RequiredTarget_Month: w.requiredTarget ?? w.requiredTarget_Month ?? "—",
      ActualPct_TillStatus: w.actualPlanPct_Week ?? w.actualPct_TillStatus ?? "—",
      CurrentRatePerDay: w.currentRatePerDay ?? "—",
      RequiredRatePerDay: w.requiredRatePerDay ?? "—",
    }));
  }, [data]);

  if (isFetching) {
    return (
      <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ fontSize: 13, color: "red" }}>
          Failed to load weekly data: {error?.data?.message ?? error?.error ?? "Unknown error"}
        </Typography>
      </Box>
    );
  }

  if (!rows.length) {
    return <Typography sx={{ fontSize: 13, color: "#6B7280" }}>No weekly data.</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box 
          sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            gap: 1,
            alignItems: "center",
        }}>
        <Box sx={{ textAlign: "left" }}>
        <Header title="Week" align="left" />
        </Box>

        <Box sx={{ textAlign: "right" }}>
        <Header title="Baseline Planned Capex" subtitle="(INR Cr)" align="right" />
        </Box>

        <Box sx={{ textAlign: "right" }}>
        <Header title="BCWP" subtitle="(INR Cr)" align="right" />
        </Box>

        <Box sx={{ textAlign: "right" }}>
        <Header title="Baseline Plan" subtitle="(%)" align="right" />
        </Box>

        <Box sx={{ textAlign: "right" }}>
        <Header title="BCWP till date" subtitle="(%)" align="right" />
        </Box>
      </Box>

      {rows.map((w, idx) => (
        <Box
            key={idx}
            sx={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 1,
                alignItems: "center",
                py: 0.5,
                borderRadius: 1,
                px: 0.5,
                "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
            }}
            >
            <Typography sx={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                {w.label}
            </Typography>

            <Typography sx={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                {w.BaselineCost_Month}
            </Typography>

            <Typography sx={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                {w.ActualCost_Month}
            </Typography>

            <Typography sx={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                {w.BaselinePlanPct_Month}
            </Typography>

            <Typography sx={{ fontSize: 12, textAlign: "right", color: "#6B7280", whiteSpace: "nowrap" }}>
                {w.ActualPct_TillStatus}
            </Typography>
        </Box>
      ))}
    </Box>
  );
};
export default ScheduleWeeklySection
